import React, { useEffect, useRef } from "react";
import { useReveal } from "../hooks/useReveal";

// locked1 — the accumulating-lessons form, kept for reference.
// eslint-disable-next-line no-unused-vars
const EQUATION_LATEX_LOCKED1 =
  "\\text{Magic}\\;=\\;\\textcolor{#5B4B9E}{\\left(\\sum_{i=1}^{n} w_i\\, v_i\\right)}\\;\\times\\;\\textcolor{#A00530}{\\left(1 + \\sum_{k=1}^{m} \\ell_k\\right)}";

// Colors here are the on-paper ink palette (the equation sits on light #F3EAD6);
// .ph-legend below echoes them in the lighter on-dark tones.
const EQUATION_LATEX =
  "\\text{Magic}\\;=\\;\\textcolor{#5B4B9E}{\\left(\\sum_{i=1}^{n} w_i\\, v_i\\right)}\\;\\times\\;\\textcolor{#A00530}{(1 + \\ell)^{T}}";

const LabsPhilosophySection = () => {
  const [headRef, headIn] = useReveal();
  const [paperRef, paperIn] = useReveal();
  const [quoteOneRef, quoteOneIn] = useReveal();
  const [quoteTwoRef, quoteTwoIn] = useReveal();
  const equationRef = useRef(null);

  // Render the equation with KaTeX (loaded from CDN); retry until the script is ready.
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !equationRef.current) return;
      if (typeof window.katex === "undefined") {
        window.setTimeout(render, 60);
        return;
      }
      window.katex.render(EQUATION_LATEX, equationRef.current, { displayMode: true, throwOnError: false });
    };
    render();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="philosophy"
      className="relative pt-12 pb-28 sm:pt-16 sm:pb-36 text-paper overflow-hidden"
      style={{ background: "radial-gradient(120% 80% at 50% 0%,#2E2668,#1B1540 55%,#14103A)" }}
    >
      <div
        className="absolute -top-40 left-[6%] w-[32rem] h-[32rem] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle,#6C4FD0,transparent 62%)" }}
      />
      <div
        className="absolute -bottom-40 right-[4%] w-[28rem] h-[28rem] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle,#D8073C,transparent 62%)" }}
      />
      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
        <div ref={headRef} className={`reveal text-center ${headIn ? "in" : ""}`}>
          <span className="kicker text-violet-c">Philosophy</span>
          <h2 className="mt-5 font-display font-normal text-4xl sm:text-5xl tracking-tight leading-[1.06]">
            Why we call it a <span className="italic text-rose">lab.</span>
          </h2>
          <p className="ph-lead">
            Not your typical company, and not by accident. In a lab, a hypothesis is worth nothing
            until evidence backs it. Being wrong is welcome here. It's how we learn.
          </p>
        </div>

        <div className="ph-rule" />

        <div ref={paperRef} className={`reveal text-center ${paperIn ? "in" : ""}`}>
          <div className="paper">
            <div id="ph-eq" ref={equationRef} />
          </div>
          <div className="ph-legend">
            <span className="it">
              <span className="sym" style={{ color: "#B9A8E8" }}>v&#7522;</span> the idea
            </span>
            <span className="it">
              <span className="sym" style={{ color: "#B9A8E8" }}>w&#7522;</span> the weight of an idea, earned by its own merit
            </span>
            <span className="it">
              <span className="sym" style={{ color: "#D8073C" }}>&#8467;</span> what being wrong teaches us
            </span>
            <span className="it">
              <span className="sym" style={{ color: "#D8073C" }}>T</span> how many times we're willing to be wrong
            </span>
          </div>
        </div>

        <div className="ph-rule" style={{ marginTop: "52px" }} />

        <div className="ph-cols">
          <blockquote ref={quoteOneRef} className={`ph-q reveal ${quoteOneIn ? "in" : ""}`} style={{ margin: 0 }}>
            <span className="qmark" style={{ color: "#B9A8E8" }}>&ldquo;</span>
            <p className="quote">
              No one sits above the <em style={{ color: "#B9A8E8" }}>idea</em>.
            </p>
            <p className="qmeta" style={{ color: "#B9A8E8" }}>The structure</p>
            <p className="qsub">
              Titles carry no weight here. The merit behind an idea is the only thing that does.
            </p>
          </blockquote>
          <blockquote ref={quoteTwoRef} className={`ph-q reveal ${quoteTwoIn ? "in" : ""}`} style={{ margin: 0, transitionDelay: ".12s" }}>
            <span className="qmark" style={{ color: "#D8073C" }}>&ldquo;</span>
            <p className="quote">
              Being wrong is <em style={{ color: "#D8073C" }}>cheap</em> here. That's the whole advantage.
            </p>
            <p className="qmeta" style={{ color: "#D8073C" }}>The condition</p>
            <p className="qsub">
              Every mistake teaches us something. The more often we're willing to be wrong, the more
              the work compounds.
            </p>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default LabsPhilosophySection;